import React, { useCallback, useState, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { ArrowBackIcon, EditIcon } from '@toasttab/buffet-pui-icons'

import { ButtonType } from 'src/apollo/sites'
import {
  formatDeliveryAddressLine1,
  formatDeliveryAddressLine2
} from 'src/public/components/online_ordering/addressUtils'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'

import { BrandedButton } from 'src/shared/components/common/button'
import { t } from 'src/translations'

import { ScreenProps, useNavigateToCheckout } from './DeliveryModal'
import { isSavedAddress } from './deliveryAddressUtils'
import { useDelivery } from 'src/public/components/online_ordering/DeliveryContext'
import InputField from 'src/shared/components/common/form_input/InputField'

import { reportErrorMessageWithData } from 'src/lib/js/clientError'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import { trackDiningOptionChangedV2 } from '../dining_behavior_toggle/fulfillmentTrackingUtils'
import useTracker from 'src/lib/js/hooks/useTracker'
import { getCountryAddressConfig } from 'src/public/components/online_ordering/address_configs'
import { useOptionalRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

interface AddressFormData {
  intermediateAddress2: string
  intermediateSaveNewAddress: boolean
  intermediateAddressLabel: string
  intermediateDeliveryInstructions: string
}

/**
 * This screen can support two scenarios - completing the delivery flow, or entering
 * from Checkout to modify your current sessions delivery address.
 */
const AddressConfirmScreen = ({
  closeModal,
  goToScreen,
  deliveryAddress,
  inRangeAddresses,
  outOfRangeAddresses,
  trackingSource,
  shouldNavigateToCheckoutOnComplete,
  allowBackNav
}: ScreenProps) => {
  const { updateCartFulfillment, fulfillmentData } = useFulfillment()
  const navigateToCheckout = useNavigateToCheckout()
  const {
    saveSessionAddress,
    setSaveSessionAddress,
    sessionAddressLabel,
    setSessionAddressLabel,
    setSavedAddressUsed
  } = useDelivery()
  const [loading, setLoading] = useState<boolean>(false)
  const formMethods = useForm<AddressFormData>({
    defaultValues: {
      intermediateAddress2: deliveryAddress?.address2 || '',
      intermediateSaveNewAddress: saveSessionAddress,
      intermediateAddressLabel: sessionAddressLabel,
      intermediateDeliveryInstructions: deliveryAddress?.notes || ''
    }
  })
  const ooRestaurant = useOptionalRestaurant()?.ooRestaurant
  const addressConfig = getCountryAddressConfig(ooRestaurant?.i18n?.country)
  const tracker = useTracker()
  const { intlAddressCountryLogicErrorHandling } = useFlags()

  const { getValues, trigger } = formMethods

  // Watch form values for UI reactivity

  // This handler ensures all state external to the flow components is synchronized.
  const confirmHandler = useCallback(async () => {
    // prevent any race conditions before default fulfillmentData is set by context at app init.
    // in practice, this should rarely ever be hit.
    if (!fulfillmentData?.cartFulfillmentData) {
      return
    }

    if (deliveryAddress) {
      // Validate form before proceeding
      const isValid = await trigger()
      if (!isValid) {
        return // Stop if validation fails
      }
      setLoading(true)
      const {
        intermediateSaveNewAddress,
        intermediateAddressLabel,
        intermediateDeliveryInstructions,
        intermediateAddress2
      } = getValues()
      const compositeAddress = {
        ...deliveryAddress,
        address2: intermediateAddress2,
        notes: intermediateDeliveryInstructions
      }

      // sync all changes to the session
      try {
        await updateCartFulfillment({
          fulfillmentType: fulfillmentData.cartFulfillmentData.fulfillmentType,
          fulfillmentDateTime:
            fulfillmentData.cartFulfillmentData.fulfillmentDateTime,
          diningOptionBehavior: DiningOptionBehavior.Delivery,
          deliveryInfo: compositeAddress
        })
        setSaveSessionAddress(intermediateSaveNewAddress)
        setSessionAddressLabel(intermediateAddressLabel)
        setSavedAddressUsed(isSavedAddress(deliveryAddress))
        trackDiningOptionChangedV2(
          tracker,
          fulfillmentData.cartFulfillmentData.fulfillmentType,
          isSavedAddress(deliveryAddress),
          trackingSource,
          DiningOptionBehavior.Delivery,
          compositeAddress
        )
        closeModal()
        if (shouldNavigateToCheckoutOnComplete) {
          navigateToCheckout()
        }
      } catch (error) {
        reportErrorMessageWithData(
          'Error updating cart fulfillment delivery info',
          { error }
        )
      } finally {
        setLoading(false)
      }
    }
  }, [
    fulfillmentData?.cartFulfillmentData,
    deliveryAddress,
    trigger,
    getValues,
    updateCartFulfillment,
    setSaveSessionAddress,
    setSessionAddressLabel,
    setSavedAddressUsed,
    tracker,
    trackingSource,
    closeModal,
    shouldNavigateToCheckoutOnComplete,
    navigateToCheckout
  ])

  // should only be on this screen completing the flow, or entering from checkout which
  // means a valid delivery address is already comitted to for fulfillment.
  useEffect(() => {
    if (!deliveryAddress) {
      if (inRangeAddresses.length > 0 || outOfRangeAddresses.length > 0) {
        goToScreen('address-hub', null)
      } else {
        goToScreen('address-search')
      }
    }
  }, [
    deliveryAddress,
    goToScreen,
    inRangeAddresses.length,
    outOfRangeAddresses.length
  ])

  const backOrEditButtonHandler = useCallback(() => {
    if (inRangeAddresses.length > 0 || outOfRangeAddresses.length > 0) {
      goToScreen('address-hub')
    } else {
      goToScreen('address-search')
    }
  }, [goToScreen, inRangeAddresses, outOfRangeAddresses])

  if (!deliveryAddress) {
    return null
  }

  return (
    <div data-testid='address-confirm-screen' className='addressConfirmScreen'>
      <div className='modalHeader'>
        {allowBackNav && (
          <button
            data-testid='confirm-back-btn'
            aria-label={t('fulfillment-general.back')}
            type='button'
            className='backArrow'
            onClick={backOrEditButtonHandler}
          >
            <ArrowBackIcon />
          </button>
        )}
        <h2 id='delivery-address-modal-title' tabIndex={-1}>
          {t('address-confirm-screen.title')}
        </h2>
      </div>
      <div className='modalContent'>
        <FormProvider {...formMethods}>
          <div className='confirmContainer'>
            <div className='selectedAddress'>
              <div className='addressText'>
                <div className='addrLine1'>
                  {intlAddressCountryLogicErrorHandling
                    ? addressConfig.formatters.formatLine1(deliveryAddress)
                    : formatDeliveryAddressLine1(deliveryAddress, false)}
                </div>
                <div className='addrLine2'>
                  {intlAddressCountryLogicErrorHandling
                    ? addressConfig.formatters.formatLine2(deliveryAddress)
                    : formatDeliveryAddressLine2(deliveryAddress, true)}
                </div>
              </div>
              <button
                type='button'
                className='editAddressButton'
                onClick={backOrEditButtonHandler}
                aria-label={t('address-confirm-screen.edit-address-aria')}
              >
                <EditIcon />
              </button>
            </div>
            <div className='addressInputField'>
              <p>{t('address-confirm-screen.additional-details-label')}</p>
              <InputField
                id='intermediateAddress2'
                type='text'
                label={t('address-confirm-screen.apartment-unit-optional')}
                preventFormSubmit
              />
            </div>
            <div
              data-testid='deliveryInstructions'
              className='addressInputField'
            >
              <p>{t('address-confirm-screen.delivery-instructions-label')}</p>
              <InputField
                id='intermediateDeliveryInstructions'
                type='text'
                label={t('address-confirm-screen.delivery-instructions')}
                preventFormSubmit
              />
            </div>
          </div>
        </FormProvider>
      </div>
      <div className='footer'>
        <BrandedButton
          onClick={closeModal}
          className='cancelButton'
          data-testid='address-confirm-cancel'
        >
          {t('fulfillment-general.cancel')}
        </BrandedButton>
        <BrandedButton
          variant={ButtonType.Primary}
          onClick={confirmHandler}
          testId='address-confirm-confirm'
        >
          {t('address-confirm-screen.confirm-address')}
        </BrandedButton>
      </div>
      {loading && <LoadingSpinnerOverlay withSubtleBorderRadius />}
    </div>
  )
}

export default AddressConfirmScreen
